import type { AxiosError } from 'axios';

const cancelled = () => new DOMException('Proofreading submission cancelled', 'AbortError');

const wait = (delayMs: number, signal: AbortSignal) => new Promise<void>((resolve, reject) => {
  if (signal.aborted) { reject(cancelled()); return; }
  const abort = () => { clearTimeout(timer); reject(cancelled()); };
  const timer = setTimeout(() => { signal.removeEventListener('abort', abort); resolve(); }, delayMs);
  signal.addEventListener('abort', abort, { once: true });
});

/** Only the API's pre-admission quota rejection is safe to retry for a paid POST. */
const quotaDelay = (error: unknown): number | null => {
  const response = (error as AxiosError<{ detail?: string }>)?.response;
  if (response?.status !== 429 || response.data?.detail !== 'Request quota exceeded') return null;
  const headers = response.headers;
  const raw = headers?.['retry-after'];
  const seconds = Number(raw);
  // Malformed/unbounded instructions fail visibly instead of retrying early.
  return Number.isFinite(seconds) && seconds > 0 && seconds <= 300 ? seconds * 1000 : null;
};

/** A single submission queue shares backoff across all chapters in one user action. */
export const createProofreadAdmissionQueue = (
  signal: AbortSignal,
  onRateLimited?: () => void,
) => {
  let tail: Promise<unknown> = Promise.resolve();
  return <T>(request: () => Promise<T>): Promise<T> => {
    const result = tail.then(async () => {
      for (let attempt = 0; ; attempt += 1) {
        if (signal.aborted) throw cancelled();
        try { return await request(); }
        catch (error) {
          const delay = quotaDelay(error);
          if (signal.aborted) throw cancelled();
          if (delay === null || attempt >= 3) throw error;
          onRateLimited?.();
          await wait(delay, signal);
        }
      }
    });
    // A refused chapter must not strand the remaining unsubmitted chapters.
    tail = result.catch(() => undefined);
    return result;
  };
};
